import { RTW_BAND } from './firmware.js';
/** Small, fast, seedable PRNG (mulberry32) — keeps simulated runs reproducible. */
export function createRng(seed) {
    let a = seed >>> 0;
    return () => {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
/** Box-Muller transform for Gaussian shadowing noise. */
function gaussian(rng) {
    const u = Math.max(rng(), Number.EPSILON);
    const v = rng();
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}
export function createSyntheticSite(options = {}) {
    const width = options.width_m ?? 30;
    const height = options.height_m ?? 20;
    const defaultAps = [
        { bssid: '02:00:00:00:00:01', ssid: 'Lab-AP-1', x: 0, y: 0, channel: 1, band: RTW_BAND.RTW_BAND_ON_24G, calibratedRssi: -40 },
        { bssid: '02:00:00:00:00:02', ssid: 'Lab-AP-2', x: width, y: 0, channel: 6, band: RTW_BAND.RTW_BAND_ON_24G, calibratedRssi: -40 },
        { bssid: '02:00:00:00:00:03', ssid: 'Lab-AP-3', x: 0, y: height, channel: 11, band: RTW_BAND.RTW_BAND_ON_24G, calibratedRssi: -40 },
        { bssid: '02:00:00:00:00:04', ssid: 'Lab-AP-4', x: width, y: height, channel: 36, band: RTW_BAND.RTW_BAND_ON_5G, calibratedRssi: -42 },
        { bssid: '02:00:00:00:00:05', ssid: 'Lab-AP-5', x: width / 2, y: height / 2, channel: 44, band: RTW_BAND.RTW_BAND_ON_5G, calibratedRssi: -42 },
        { bssid: '02:00:00:00:00:06', ssid: 'Lab-AP-6', x: width / 2, y: 0, channel: 149, band: RTW_BAND.RTW_BAND_ON_5G, calibratedRssi: -42 },
    ];
    return {
        width_m: width,
        height_m: height,
        pathLossExponent: options.pathLossExponent ?? 2.8,
        aps: options.aps ?? defaultAps,
    };
}
/**
 * Produce a scan result for a receiver at (x, y) using the log-distance path-loss model:
 *   RSSI(d) = calibratedRssi - 10 * n * log10(d)
 */
export function simulateScan(site, x, y, rng, options = {}) {
    const noiseDb = options.noiseDb ?? 2.5;
    const sensitivity = options.sensitivityDbm ?? -92;
    const missProbability = options.missProbability ?? 0.03;
    const aps = [];
    for (const ap of site.aps) {
        const distance = Math.max(1, Math.hypot(ap.x - x, ap.y - y));
        const ideal = ap.calibratedRssi - 10 * site.pathLossExponent * Math.log10(distance);
        const rssi = Math.round(ideal + (noiseDb > 0 ? gaussian(rng) * noiseDb : 0));
        if (rssi < sensitivity)
            continue;
        if (rng() < missProbability)
            continue;
        const entry = {
            bssid: ap.bssid,
            ssid: ap.ssid,
            channel: ap.channel,
            band: ap.band,
            security: 0x40_0004, // WPA2_SECURITY | AES_ENABLED
            wireless_mode: 1 << 5, // RTW_80211_AX
            is_beacon: 1,
        };
        if (options.beaconReport) {
            // 802.11k encodes level as RCPI (0.5 dBm steps from -110 dBm) and SNR as RSNI.
            entry.rcpi = Math.max(0, Math.min(220, Math.round((rssi + 110) * 2)));
            entry.rsni = Math.max(0, Math.min(255, Math.round((rssi + 95 + 10) * 2)));
        }
        else {
            entry.signal_strength = rssi;
        }
        aps.push(entry);
    }
    return aps;
}
/** Grid of calibration points inset from the walls by half the spacing. */
export function gridPoints(site, spacing) {
    const points = [];
    for (let x = spacing / 2; x <= site.width_m; x += spacing) {
        for (let y = spacing / 2; y <= site.height_m; y += spacing) {
            points.push({ x: Math.round(x * 100) / 100, y: Math.round(y * 100) / 100 });
        }
    }
    return points;
}
/** Points along a closed rectangular walk, used to generate test traces. */
export function walkPoints(site, steps) {
    const margin = Math.min(site.width_m, site.height_m) * 0.2;
    const corners = [
        { x: margin, y: margin },
        { x: site.width_m - margin, y: margin },
        { x: site.width_m - margin, y: site.height_m - margin },
        { x: margin, y: site.height_m - margin },
    ];
    const points = [];
    for (let i = 0; i < steps; i++) {
        const t = (i / steps) * corners.length;
        const from = corners[Math.floor(t) % corners.length];
        const to = corners[(Math.floor(t) + 1) % corners.length];
        const f = t - Math.floor(t);
        points.push({
            x: Math.round((from.x + (to.x - from.x) * f) * 100) / 100,
            y: Math.round((from.y + (to.y - from.y) * f) * 100) / 100,
        });
    }
    return points;
}
//# sourceMappingURL=simulation.js.map